/**
 * Get visual information for a process stage
 * @param {string} stage - The process stage identifier
 * @returns {Object} Object with color, label, and short label for the stage
 */
export const getProcessStageInfo = (stage) => {
    let processStyle = { 
        color: 'rgba(189, 189, 189, 0.4)', 
        borderColor: 'rgb(189, 189, 189)',
        label: 'Processing', 
        shortLabel: '...' 
    };
    
    switch(stage) {
        case 'LEVEL0_AI_COMPLETE':
            processStyle =  { 
                color: 'rgba(140, 158, 255, 0.8)', 
                borderColor: 'rgb(140, 158, 255)',
                label: 'AI Generated', 
                shortLabel: 'AI' 
            };
            break;
        case 'LEVEL1_SEVAK_COMPLETE':
            processStyle =  { 
                color: 'rgba(100, 181, 246, 0.8)', 
                borderColor: 'rgb(100, 181, 246)',
                label: 'Level 1 Complete', 
                shortLabel: 'L1' 
            };
            break;
        case 'LEVEL2_SEVAK_COMPLETE':
            processStyle =  { 
                color: 'rgba(77, 182, 172, 0.8)', 
                borderColor: 'rgb(77, 182, 172)',
                label: 'Level 2 Complete', 
                shortLabel: 'L2' 
            };
            break;
        case 'LEVEL3_SEVAK_COMPLETE':
            processStyle =  { 
                color: 'rgba(129, 199, 132, 0.8)', 
                borderColor: 'rgb(129, 199, 132)',
                label: 'Level 3 Complete', 
                shortLabel: 'L3' 
            };
            break;
        case 'PUBLISHED':
            processStyle =  { 
                color: 'rgba(102, 187, 106, 0.8)', 
                borderColor: 'rgb(102, 187, 106)',
                label: 'Published', 
                shortLabel: 'P' 
            };
            break;
        default:
            processStyle =  { 
                color: 'rgba(189, 189, 189, 0.4)', 
                borderColor: 'rgb(189, 189, 189)',
                label: 'Processing', 
                shortLabel: '...' 
            };
            break;
    }
};

// Stage chip colors from the sign-in palette, one set per mode.
// Each passes 4.5:1 as chip text on its own 12% tint (callers append "20" for the background).
const STAGE_CHIP_COLORS = {
    light: { ai: '#875609', level1: '#3d6475', level2: '#7a3f6b', level3: '#2f5a4c', published: '#3d6a32', failed: '#9e2a3a', other: '#625a4d' },
    dark: { ai: '#e0a94a', level1: '#86aec0', level2: '#d79ac6', level3: '#7fb8a4', published: '#8fbf7d', failed: '#f08a96', other: '#b3a690' },
};

/**
 * Get the color for a language chip based on its process stage
 * @param {string} processStage - The process stage of the language
 * @param {string} mode - Theme mode, 'light' or 'dark'
 * @returns {string} The color code for the chip's text and border
 */
export const getChipColor = (processStage, mode = 'light') => {
    const colors = STAGE_CHIP_COLORS[mode === 'dark' ? 'dark' : 'light'];
    switch(processStage) {
        case 'PUBLISHED':
            return colors.published;
        case 'LEVEL3_SEVAK_COMPLETE':
            return colors.level3;
        case 'LEVEL3_WIP':
        case 'LEVEL2_SEVAK_COMPLETE':
            return colors.level2;
        case 'LEVEL2_WIP':
        case 'LEVEL1_SEVAK_COMPLETE':
            return colors.level1;
        case 'LEVEL1_WIP':
        case 'LEVEL0_AI_COMPLETE':
            return colors.ai;
        default:
            return colors.other; // Unknown/processing
    }
};

/**
 * Get the color for a text file language chip based on its process stage
 * @param {string} processStage - The process stage of the language
 * @param {string} mode - Theme mode, 'light' or 'dark'
 * @returns {string} The color code for the chip's text and border
 */
export const getTextChipColor = (processStage, mode = 'light') => getChipColor(processStage, mode);

/**
 * Get user-friendly label for a process stage
 * @param {string} processStage - The process stage
 * @returns {string} A user-friendly label for the stage
 */
export const getProcessStageLabel = (processStage) => {
    switch(processStage) {
        case 'PUBLISHED':
            return 'Published';
        case 'LEVEL3_SEVAK_COMPLETE':
            return 'Level 3 Complete';
        case 'LEVEL2_SEVAK_COMPLETE':
            return 'Level 2 Complete';
        case 'LEVEL1_SEVAK_COMPLETE':
            return 'Level 1 Complete';
        case 'LEVEL0_AI_COMPLETE':
            return 'AI Complete';
        case 'LEVEL3_WIP':
            return 'Level 3 In Progress';
        case 'LEVEL2_WIP':
            return 'Level 2 In Progress';
        default:
            return 'Generating';
    }
};
/**
 * Get user-friendly label for a process stage
 * @param {string} processStage - The process stage
 * @returns {string} A user-friendly label for the stage
 */
export const getChipLabel = (language, processStage) => {
    switch(processStage) {
        case 'PUBLISHED':
            return language + ' - P';
        case 'LEVEL3_SEVAK_COMPLETE':
            return language + ' - L3';
        case 'LEVEL2_SEVAK_COMPLETE':
            return language + ' - L2';
        case 'LEVEL1_SEVAK_COMPLETE':
            return language + ' - L1';
        case 'LEVEL0_AI_COMPLETE':
            return language + ' - AI';
        case 'LEVEL3_WIP':
            return language + ' - L2';
        case 'LEVEL2_WIP':
            return language + ' - L1';
        default:
            return language + ' - G';;
    }
};

// Get color for process stage
export const getProcessStageColor = (stage) => {
    if (!stage) return '#757575';

    if (stage.includes('LEVEL0')) return '#ff9800';
    if (stage.includes('LEVEL1')) return '#2196f3';
    if (stage.includes('LEVEL2')) return '#9c27b0';
    if (stage.includes('LEVEL3')) return '#3f51b5';
    if (stage.includes('PUBLISHED')) return '#4caf50';

return '#757575';
};

export const isSourceLanguage = (languageData) => {
    return languageData?.translationStatus === 'SOURCE_LANGUAGE';
};

export const getTextProcessStage = (languageData) => {
    return languageData?.processStage || 'LEVEL1_WIP';
};

export const getEffectiveTextProcessStage = (languageData) => {
    const baseStage = getTextProcessStage(languageData);
    const translationStatus = languageData?.translationStatus;

    if (translationStatus === 'IN_PROGRESS' || translationStatus === 'PENDING') {
        return 'UNKNOWN';
    }

    if (translationStatus === 'COMPLETED') {
        // Use the actual processStage if human review has progressed beyond AI level;
        // otherwise the translation just finished AI generation.
        return baseStage || 'LEVEL0_AI_COMPLETE';
    }

    return baseStage;
};

// Book (IDML) per-language process stages: TRANSLATING/REVIEW/EXPORTED/TRANSLATION_FAILED
// (a different vocabulary from the text/video LEVEL* stages, so these get their own helpers)
export const getBookLanguageStageLabel = (processStage) => {
    switch (processStage) {
        case 'EXPORTED':
            return 'Exported';
        case 'REVIEW':
            return 'In Review';
        case 'TRANSLATING':
            return 'Translating';
        case 'TRANSLATION_FAILED':
            return 'Translation Failed';
        default:
            return 'Pending';
    }
};

export const getBookLanguageChipColor = (processStage, mode = 'light') => {
    const colors = STAGE_CHIP_COLORS[mode === 'dark' ? 'dark' : 'light'];
    switch (processStage) {
        case 'EXPORTED':
            return colors.published;
        case 'REVIEW':
            return colors.level1;
        case 'TRANSLATING':
            return colors.ai;
        case 'TRANSLATION_FAILED':
            return colors.failed;
        default:
            return colors.other; // Pending/unknown
    }
};

export const getBookLanguageChipLabel = (languageName, processStage) => {
    switch (processStage) {
        case 'EXPORTED':
            return `${languageName} - EXP`;
        case 'REVIEW':
            return `${languageName} - REV`;
        case 'TRANSLATING':
            return `${languageName} - TRN`;
        case 'TRANSLATION_FAILED':
            return `${languageName} - ERR`;
        default:
            return `${languageName} - ...`;
    }
};

export const getTextChipLabel = (languageName, languageData) => {
    if (isSourceLanguage(languageData)) {
        return `${languageName} - SRC`;
    }

    // Use a text-file-specific mapping so video file chips are unaffected.
    const stage = getEffectiveTextProcessStage(languageData);
    switch (stage) {
        case 'PUBLISHED':
            return `${languageName} - P`;
        case 'LEVEL3_SEVAK_COMPLETE':
            return `${languageName} - L3`;
        case 'LEVEL2_SEVAK_COMPLETE':
        case 'LEVEL3_WIP':
            return `${languageName} - L2`;
        case 'LEVEL1_SEVAK_COMPLETE':
        case 'LEVEL2_WIP':
            return `${languageName} - L1`;
        case 'LEVEL0_AI_COMPLETE':
        case 'LEVEL1_WIP':
            return `${languageName} - AI`;
        default:
            return `${languageName} - G`;
    }
};